import { isServer } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Alert } from '@websrc/App/Components/Alert/Alert'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import {
  DownloadLanguage,
  DownloadLanguages,
  getTextForDownloadType,
} from '@websrc/App/Components/Download/DownloadLanguage'
import { DownloadSpecs } from '@websrc/App/Components/Download/DownloadSpecs'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { getDevices } from '@websrc/App/Helpers/getDevices'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { isDefined } from '@websrc/DataMappers/Helpers/tsPattern'
import { mapDownload } from '@websrc/DataMappers/mapDownload'
import clsx from 'clsx'
import { box, gridContainer, gridItem, select, styles, typography } from 'leds'
import React, { ChangeEvent, FC, useCallback, useContext, useEffect, useReducer } from 'react'
import { match } from 'ts-pattern'

export type DownloadProps = {
  data: ReturnType<typeof mapDownload>
  useBoxTypeText?: boolean
}

type DownloadDevice = DownloadProps['data']['items'][0]

type State = {
  device?: DownloadDevice
  language?: DownloadLanguages[0]
  sameDevice: boolean
}

type BaseAction = {
  type: 'base'
  payload: State
}

type LanguageAction = {
  type: 'language'
  payload: DownloadLanguages[0]
}

type Action = BaseAction | LanguageAction

export const Download: FC<DownloadProps> = ({ data, useBoxTypeText }) => {
  const t = useStaticText()
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const [{ device, language, sameDevice }, setState] = useReducer(
    (state: State, action: Action) =>
      match(action)
        .with({ type: 'base' }, ({ payload }) => payload)
        .with({ type: 'language' }, ({ payload }) => ({ ...state, language: payload }))
        .exhaustive(),
    { sameDevice: true }
  )

  const matchDevice = useCallback(
    (devices: string[]) => {
      let [foundDevice] = data.items.filter((d) => devices.includes(d.software.device))

      // if we can't match device, fallback to first item
      if (!foundDevice) {
        foundDevice = data.items[0]
      }

      setState({
        type: 'base',
        payload: {
          device: foundDevice,
          language: foundDevice.software.language[0],
          sameDevice: getDevices().includes(foundDevice.software.device),
        },
      })
    },
    [data.items]
  )

  useEffect(() => {
    if (!isServer) {
      matchDevice(getDevices())
    }
  }, [matchDevice])

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    matchDevice([e.target.value])

    matomo.trackEvent(
      'engagement',
      matomoPageTemplate,
      ['page body'],
      ['change platform', e.target.value],
      'drop-down list'
    )
  }

  const updateLanguage = (updateLang: DownloadLanguages[0]) => {
    setState({ type: 'language', payload: updateLang })

    matomo.trackEvent(
      'engagement',
      matomoPageTemplate,
      ['page body'],
      ['change variant', updateLang.key],
      'drop-down list'
    )
  }

  const downloadTitle = match(language)
    .with({ version: isDefined }, ({ version }) => `${data.header.title} v. ${version}`)
    .otherwise(() => data.header.title)

  return (
    <div className={box({ align: 'center', type: useBoxTypeText ? 'text' : undefined })}>
      <div className={box({ content: 'center' })}>
        <div className={box({ type: 'logo' })}>
          <Asset className={box({ type: 'logo' })} thumbnail={data.header.image} width={110} />
        </div>
        <h1
          className={clsx(typography({ size: 'xl2', weight: 'bold' }), styles({ marginLeft: 25 }))}
        >
          {downloadTitle}
        </h1>
      </div>
      <div className={clsx(gridContainer(), styles({ marginY: 40 }))}>
        <div className={clsx(gridItem({ columns: 'two' }), styles({ height: 'inherit' }))}>
          <select
            className={clsx(select({ type: 'primary' }), styles({ height: 'full' }))}
            data-testid={'device-select'}
            onChange={handleSelect}
            value={device?.software.device}
          >
            {data.items.map(({ software }, key) => (
              <option key={key} value={software.device}>
                {software.device}
              </option>
            ))}
          </select>
        </div>
        {device && language && (
          <DownloadLanguage
            className={clsx(
              gridItem({ columns: 'two' }),
              box({ content: 'right', align: 'center' })
            )}
            data-testid={'download-languages'}
            languages={device.software.language}
            languageSelected={language}
            onDownload={() =>
              matomo.trackEvent(
                'engagement',
                matomoPageTemplate,
                ['page body'],
                [
                  `${getTextForDownloadType(language.__downloadType, t)} - ${
                    device.software.device
                  }`,
                  downloadTitle,
                ],
                'button'
              )
            }
            updateLanguage={updateLanguage}
          />
        )}
      </div>

      {!sameDevice && (
        <Alert highlight={true} type={'warning'}>
          <Markdown markdown={t.downloads.download_device_does_not_match} />
        </Alert>
      )}

      {device?.notes && <Markdown markdown={device.notes} />}

      {/* load specs depending on device and language */}
      {device && language && <DownloadSpecs data={data} device={device} language={language} />}

      {data.description && (
        <Markdown className={typography({ align: 'left' })} markdown={data.description} />
      )}
    </div>
  )
}
